import { Component, OnInit, computed, inject, input, Signal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, RouterLink, RouterModule, RouterOutlet } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ImageFallbackDirective } from '../img-fallback.directive';
import { Project, Participant, Report, Package, Assessment } from '../models';
import { countBy, filter, get, size } from 'lodash-es';
import { LocaleNamePipe } from '../locale-name-pipe';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { ReportDomainService } from '../domain-services/report-domain.service';
import { AssessmentDomainService } from '../domain-services/assessment-domain.service';
import { PackageDomainService } from '../domain-services/package-domain.service';
import { catchError, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserPermissionsService } from '../user-permissions.service';

@Component({
  selector: 'app-project-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatToolbarModule,
    MatButtonModule,
    RouterModule,
    MatDividerModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ImageFallbackDirective,
    RouterLink,
    LocaleNamePipe,
    RouterOutlet,
  ],
  templateUrl: './project-detail.component.html',
  styleUrls: ['./project-detail.component.scss'],
})
export class ProjectDetailComponent implements OnInit {
  reports = input<Report[]>();
  packages = input<Package[]>();

  private readonly projectDomain = inject(ProjectDomainService);
  private readonly reportDomain = inject(ReportDomainService);
  private readonly assessmentDomain = inject(AssessmentDomainService);
  private readonly packageDomain = inject(PackageDomainService);
  readonly route = inject(ActivatedRoute);
  private readonly snackBar = inject(MatSnackBar);
  readonly permissions = inject(UserPermissionsService);

  // New notification email being added
  addNotificationEmail = signal<string>('');

  // Prefer the signal-based current project; fall back to
  // the resolver data for the initial navigation.
  project = computed<Project>(() => {
    const current = this.projectDomain.currentProject();
    if (current) {
      return current;
    }
    return this.route.snapshot.data['project'] as Project;
  });

  projectReports = computed<Report[]>(() => {
    return this.reportDomain.projectReports() ?? this.reports() ?? [];
  });

  projectPackages = computed<Package[]>(() => {
    return this.packageDomain.projectPackages() ?? this.packages() ?? [];
  });

  // Invites and ID generation are only offered once the project has at
  // least one report or package assigned, mirroring the report tiles
  // rendered further down the page.
  hasAssignedReports = computed<boolean>(() => {
    return this.projectReports().length > 0 || this.projectPackages().length > 0;
  });

  participantCount = computed(() => {
    const allAssessments = this.assessmentDomain.allAssessments();
    if (!allAssessments) {
      return 0;
    }

    const projectAssessments = filter(allAssessments, { projectId: this.project().id });
    return size(countBy(projectAssessments, 'participantId'));
  });

  unclaimedIdsCount = computed(() => this.projectDomain.unclaimedIdsCount());

  ngOnInit(): void {
    const projectId = this.project()?.id ?? this.route.snapshot.paramMap.get('projectId');
    if (projectId) {
      if (!this.projectDomain.currentProject()) {
        this.projectDomain
          .loadProject(projectId)
          .pipe(catchError(() => of(null)))
          .subscribe();
      }

      if (!this.reportDomain.projectReports()) {
        this.reportDomain
          .loadProjectReports(projectId)
          .pipe(catchError(() => of([])))
          .subscribe();
      }

      if (!this.packageDomain.projectPackages()) {
        this.packageDomain
          .loadProjectPackages(projectId)
          .pipe(catchError(() => of([])))
          .subscribe();
      }

      if (!this.assessmentDomain.allAssessments()) {
        this.assessmentDomain
          .loadAssessments()
          .pipe(catchError(() => of([])))
          .subscribe();
      }

      if (this.permissions.canModify()) {
        this.projectDomain
          .loadProjectUnclaimedIds(projectId)
          .pipe(catchError(() => of([])))
          .subscribe();
      }
    }
  }

  downloadUnclaimedIds(): void {
    const project = this.project();
    const projectId = project?.id ?? this.route.snapshot.paramMap.get('projectId');
    if (!projectId) {
      return;
    }

    const downloadName = this.buildUnclaimedIdsFilename(project?.name);

    this.projectDomain.downloadProjectUnclaimedIdsCsv(projectId).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  }

  // Unused-id exports follow the convention:
  //   'NewHoganIds - {DDMMYYYY} - {project name}.csv'
  private buildUnclaimedIdsFilename(projectName: string | null | undefined): string {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear());
    const date = `${day}${month}${year}`;
    const name = (projectName ?? '').trim();

    const base = ['NewHoganIds', date, name].filter((part) => part !== '').join(' - ');
    return `${this.sanitizeFilename(base)}.csv`;
  }

  private sanitizeFilename(value: string): string {
    // Replace characters not permitted in filenames across platforms.
    return value.replace(/[\\/:*?"<>|]/g, '_').trim();
  }

  removeNotificationEmail(slot: 1 | 2 | 3 | 4): void {
    const current = this.project();
    const field = `email${slot}` as 'email1' | 'email2' | 'email3' | 'email4';
    const updated: Project = { ...current, [field]: null };

    this.projectDomain.updateProject(updated).subscribe({
      error: (err) => this.notifySaveFailed(err),
    });
  }

  onAddNotificationEmailInput(value: string): void {
    this.addNotificationEmail.set(value);
  }

  addNotificationEmailClick(): void {
    const value = this.addNotificationEmail().trim();
    if (!value || !this.isValidEmail(value)) {
      return;
    }

    const current = this.project();
    const updated: Project = { ...current };

    const fields: Array<'email1' | 'email2' | 'email3' | 'email4'> = [
      'email1',
      'email2',
      'email3',
      'email4',
    ];

    let assigned = false;
    for (const field of fields) {
      if (!updated[field]) {
        updated[field] = value;
        assigned = true;
        break;
      }
    }

    if (!assigned) {
      return;
    }

    this.projectDomain.updateProject(updated).subscribe({
      next: () => this.addNotificationEmail.set(''),
      error: (err) => this.notifySaveFailed(err),
    });
  }

  // PUT /projects/{id} revalidates the project name on every update, so a
  // project created with a name the API's TryChangeName rejects can never save
  // notification emails. Renaming it is the only way out, so say that rather
  // than failing silently, which is what these calls did before.
  private notifySaveFailed(error: unknown): void {
    const message =
      error instanceof HttpErrorResponse && error.status === 422
        ? 'Could not save. This project\'s name contains characters the server does not accept — rename the project first, then add notification emails.'
        : 'Could not save notification emails. Please try again.';

    this.snackBar.open(message, 'Close', { duration: 8000 });
  }

  private isValidEmail(email: string): boolean {
    // Simple email pattern: some chars + @ + some chars + . + some chars
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  get = get;
}
